import type {
  SearchParameterElement,
  SearchParameterName,
  SearchParameters,
  SearchParameterValues,
} from '../searchParameters';

/**
 * One parameter, on one search, named once.
 *
 * A parameter is the handful of questions a filter control asks about the
 * parameter it sets — its value, whether the search states it, whether a value
 * is selected, whether the search offers it at all — plus the ways to change it.
 * Naming it once here is the point: repeating the name at every question gives
 * each call its own chance to pass the wrong one.
 *
 * Changing a parameter returns the next search rather than changing this one.
 * The change is not worked out here: every transition goes through the search,
 * so a toggled filter is resolved by exactly the rules `Search#with` applies.
 *
 * Generic over what a transition returns, rather than importing `Search`:
 * `Search` builds these, so importing it back would be a cycle, and the
 * architecture ratchet caps those at zero (type-only imports included).
 */
export class SearchParameter<N extends SearchParameterName, S> {
  readonly #name: N;

  readonly #parameters: SearchParameters;

  readonly #isOffered: boolean;

  readonly #transition: (next: SearchParameters) => S;

  constructor(
    name: N,
    parameters: SearchParameters,
    isOffered: boolean,
    transition: (next: SearchParameters) => S
  ) {
    this.#name = name;
    this.#parameters = parameters;
    this.#isOffered = isOffered;
    this.#transition = transition;
  }

  /** The schema name this parameter is known by. */
  get name(): N {
    return this.#name;
  }

  /**
   * What the parameter is set to, in the shape the schema stores it — the raw
   * view, so `groupbyevent` reads as `'true'` rather than `true`. The return
   * type is inferred because naming it would mean exporting a type private to
   * the parameters' schema.
   */
  get value() {
    return this.#parameters.get(this.#name);
  }

  /**
   * Whether the search states this parameter, as opposed to running under the
   * value its schema row defaults to.
   */
  get isStated(): boolean {
    return this.#parameters.states(this.#name);
  }

  /**
   * Whether this search offers the parameter at all — the site's exclusions,
   * the search's mode, and its asset type and family together.
   *
   * A stated parameter is always offered: a search clears what it cannot carry
   * every time it is made, so an unoffered parameter can only be unstated.
   */
  get isOffered(): boolean {
    return this.#isOffered;
  }

  /** Whether `toggled` would remove this value rather than add it. */
  isSelected(value: SearchParameterElement<N>): boolean {
    return this.#parameters.isSelected(this.#name, value);
  }

  /** The next search, with this value added if absent and removed if present. */
  toggled(value: SearchParameterElement<N>): S {
    return this.#transition(this.#parameters.toggle(this.#name, value));
  }

  /** The next search, with this parameter set outright rather than toggled. */
  setTo(value: SearchParameterValues[N] | undefined): S {
    return this.#transition(this.#parameters.set(this.#name, value));
  }

  /** The next search, with this parameter removed and its schema default back. */
  cleared(): S {
    return this.#transition(this.#parameters.clear(this.#name));
  }
}
